import React, { useState, useEffect } from 'react';
import { ref, get } from 'firebase/database';
import { database } from '../../firebase';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalTests: number;
  newRegistrations: number;
}

export const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalTests: 0,
    newRegistrations: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const usersRef = ref(database, 'users');
      const testsRef = ref(database, 'completedTests');
      
      const [usersSnapshot, testsSnapshot] = await Promise.all([
        get(usersRef),
        get(testsRef)
      ]);

      const users = usersSnapshot.exists() ? usersSnapshot.val() : {};
      const tests = testsSnapshot.exists() ? testsSnapshot.val() : {};

      const totalUsers = Object.keys(users).length;
      const totalTests = Object.values(tests).reduce((acc: number, userTests: any) => 
        acc + Object.keys(userTests || {}).length, 0
      );

      // Calculate new registrations (last 30 days)
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      const newRegistrations = Object.values(users).filter((user: any) => 
        user.createdAt && user.createdAt > thirtyDaysAgo
      ).length;

      // Calculate active users (users who took tests in last 7 days)
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const activeUsers = Object.values(tests).filter((userTests: any) => 
        Object.values(userTests || {}).some((test: any) => 
          test.attempted_at && test.attempted_at > sevenDaysAgo
        )
      ).length;

      setStats({
        totalUsers,
        activeUsers,
        totalTests,
        newRegistrations
      });
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, icon, color }: any) => (
    <div className="bg-white p-6 rounded-lg shadow border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <div className="text-3xl">{icon}</div>
        </div>
        <div className="ml-5 w-0 flex-1">
          <dl>
            <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
            <dd className="text-3xl font-semibold text-gray-900">{value}</dd>
          </dl>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-24 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
        <p className="text-gray-600">Key metrics and system overview</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Users"
          value={stats.totalUsers}
          icon="👥"
          color="#3B82F6"
        />
        <StatCard
          title="Active Users (7 days)"
          value={stats.activeUsers}
          icon="🟢"
          color="#10B981"
        />
        <StatCard
          title="Total Tests Taken"
          value={stats.totalTests}
          icon="📝"
          color="#F59E0B"
        />
        <StatCard
          title="New Registrations (30 days)"
          value={stats.newRegistrations}
          icon="📈"
          color="#8B5CF6"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
          <div className="space-y-3">
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-3"></div>
              <span className="text-gray-600">System running normally</span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
              <span className="text-gray-600">Database connected</span>
            </div>
            <div className="flex items-center text-sm">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mr-3"></div>
              <span className="text-gray-600">Admin panel active</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <button className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded">
              📊 View Analytics
            </button>
            <button className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded">
              👥 Manage Users
            </button>
            <button className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded">
              📝 Add Questions
            </button>
            <button className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded">
              📢 Create Announcement
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};